// ---------------------------------------------------------------------------
// chart-card — bar / line / donut visualization of a stats slice
// ---------------------------------------------------------------------------
//
// Renders an HTML wrapper holding (a) a role="img" media element whose innerHTML
// is the sanitized SVG and (b) a .sr-only data table as a SIBLING outside the
// img role (so assistive tech isn't flattened out of the table). The SVG is a
// trusted template; every dynamic token is escaped by core/charts svg-util.

import { createElement } from '../../../core/dom/helpers.js';
import { barChart } from '../../../core/charts/bar-chart.js';
import { lineChart } from '../../../core/charts/line-chart.js';
import { donutChart } from '../../../core/charts/donut-chart.js';
import { chartTable } from '../../../core/charts/chart-a11y.js';
import { isAbortError } from '../../../core/http/is-abort-error.js';

/**
 * @typedef {import('../../../contracts/card-grid.js').CardDescriptor} CardDescriptor
 * @typedef {import('../../../contracts/dashboard.js').DashboardCardContext} CardContext
 * @typedef {import('../../../contracts/dashboard.js').StatsResponse} StatsResponse
 * @typedef {import('../../../contracts/card-grid.js').CardHandle} CardHandle
 * @typedef {CardDescriptor & { render: (ctx: CardContext) => CardHandle }} DashboardCard
 */

/**
 * @typedef {Object} ChartCardConfig
 * @property {string} id
 * @property {string} title
 * @property {'bar'|'line'|'donut'} chart
 * @property {(stats: StatsResponse) => { label: string, value: number }[] |
 *           { date: string, value: number }[]} select - pick + shape the series.
 * @property {string} [valueHeader] - data-table value column header.
 */

/**
 * @param {ChartCardConfig} config
 * @returns {DashboardCard}
 */
export function createChartCard(config) {
  return {
    id: config.id,
    title: config.title,
    render(ctx) {
      return mountChartCard(config, ctx);
    },
  };
}

/**
 * @param {ChartCardConfig} config
 * @param {CardContext} ctx
 * @returns {{ el: HTMLElement, destroy: () => void }}
 */
function mountChartCard(config, ctx) {
  const el = createElement('div', { class: 'dashboard-chart' });
  let destroyed = false;
  let seq = 0;

  /** @param {string} text */
  function renderMessage(text) {
    el.replaceChildren(createElement('p', { class: 'dashboard-chart__message' }, [text]));
  }

  /** @param {StatsResponse} stats */
  function render(stats) {
    const data = /** @type {any[]} */ (config.select(stats) || []);
    const idPrefix = `chart-${config.id}`;

    let svg = '';
    if (config.chart === 'bar') {
      svg = barChart(data, { idPrefix, title: config.title });
    } else if (config.chart === 'line') {
      svg = lineChart(data, { idPrefix, title: config.title });
    } else {
      svg = donutChart(data, { idPrefix, title: config.title });
    }

    const media = createElement('div', { class: 'dashboard-chart__media' });
    // Trusted chart template; dynamic tokens escaped inside the chart fns.
    media.innerHTML = svg;
    // The SVG carries aria-labelledby to its <title>/<desc>; mark the media as img.
    const svgEl = media.querySelector('svg');
    if (svgEl) svgEl.setAttribute('role', 'img');

    // Data-table fallback as a SIBLING (outside any role="img").
    const labelKey = config.chart === 'line' ? 'date' : 'label';
    const tableHtml = chartTable({
      caption: config.title,
      headers: [config.chart === 'line' ? 'Date' : 'Category', config.valueHeader || 'Value'],
      rows: data.map((d) => [String(d[labelKey] ?? ''), Number(d.value) || 0]),
    });
    const tableWrap = createElement('div', { class: 'dashboard-chart__table' });
    tableWrap.innerHTML = tableHtml; // trusted: chartTable escapes every cell

    el.replaceChildren(media, tableWrap);
  }

  function load() {
    const mine = ++seq;
    el.setAttribute('aria-busy', 'true');
    ctx.apiClient.get('/dashboard/stats', { signal: ctx.signal })
      .then((/** @type {unknown} */ raw) => {
        if (destroyed || mine !== seq) return;
        el.setAttribute('aria-busy', 'false');
        render(/** @type {StatsResponse} */ (raw) || /** @type {any} */ ({}));
      })
      .catch((/** @type {unknown} */ err) => {
        if (isAbortError(err) || destroyed || mine !== seq) return;
        el.setAttribute('aria-busy', 'false');
        ctx.logger.error('Dashboard chart load failed', { data: { card: config.id } });
        renderMessage('Couldn’t load chart.');
      });
  }

  renderMessage('Loading…');
  load();

  return {
    el,
    destroy() {
      destroyed = true;
    },
  };
}
